<template>
  <Modal v-model="visible" title="AI 知识库处理" width="640px" :mask-closable="false">
    <div class="ai-intent-generate-container">
      <div class="section">
        <h3>上传文档</h3>
        <p class="desc">上传文档至 AI 服务，生成意图表格（Excel 格式）。</p>
        <div class="upload-area">
          <Upload ref="fileUpload" :before-upload="beforeFileUpload" accept="*/*" :show-upload-list="false">
            <Button type="primary" :loading="uploading">
              <Icon type="md-cloud-upload" />
              选择文件
            </Button>
          </Upload>
          <p class="upload-tip">上传后点击生成，完成后自动下载为 .xlsx 文件。</p>
        </div>
      </div>

      <div class="section">
        <Button type="success" :loading="running" @click="runAiGenerate" :disabled="!uploadedFileId">
          <Icon type="md-play" />
          执行生成并下载
        </Button>
      </div>
    </div>

    <div slot="footer">
      <Button @click="handleCancel" :disabled="uploading || running">关闭</Button>
    </div>
  </Modal>
</template>

<script>
import axios from 'axios'
import * as XLSX from 'xlsx'
import { mapState } from 'vuex'

export default {
  name: 'AiIntentGenerateModal',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploading: false,
      running: false,
      uploadedFileId: '',
      pendingFile: null
    }
  },
  computed: {
    ...mapState('scene', ['sceneId']),
    ...mapState('user', ['company']),
    visible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    userMarker() {
      return `scene-${this.sceneId}`
    },
    difyToken() {
      // const companyId = (this.company && this.company.id) || this.company?.company_id || null
      // const tokens = {
      //   974: 'Bearer app-rjqL8dAfgRHy8XlZomWN4c1l',
      //   6951: 'Bearer app-WimJ8zDodn5wZwWrOb7qsT83',
      //   108257: 'Bearer app-KqasLv6I4xrxwaAQFIpOPmun',
      //   106764: 'Bearer app-fFvO10Bwuzuz2XaYcfAHpLG6'
      // }
      return 'Bearer app-q2qxzrMhLWAMCG5Wvr93IV6C'
    }
  },
  methods: {
    handleCancel() {
      this.visible = false
    },

    beforeFileUpload(file) {
      this.pendingFile = file
      this.uploadToDify(file)
      return false
    },

    async uploadToDify(file) {
      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('user', this.userMarker)

        const resp = await axios.post(
          'https://dify.gkscrm.com/v1/files/upload',
          formData,
          {
            headers: {
              Authorization: this.difyToken
            }
          }
        )
        const data = resp.data || {}
        const fileId = data.id || (data.data && data.data.id)
        if (!fileId) throw new Error('上传失败：未返回文件ID')
        this.uploadedFileId = fileId
        this.$Message.success('文件上传成功')
      } catch (err) {
        console.error('上传文件失败:', err)
        this.$Message.error('上传失败，请重试')
      } finally {
        this.uploading = false
      }
    },

    async runAiGenerate() {
      if (!this.uploadedFileId) return
      this.running = true
      try {
        const payload = {
          response_mode: 'streaming',
          user: this.userMarker,
          inputs: {
            usermassage: [{
              type: 'document',
              upload_file_id: this.uploadedFileId,
              transfer_method: 'local_file'
            }]
          }
        }

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 120000)
        const resp = await fetch('https://dify.gkscrm.com/v1/workflows/run', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: this.difyToken
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        })
        clearTimeout(timeoutId)
        if (!resp.ok || !resp.body) throw new Error('网络错误或无流式返回')

        const finalData = await this.readSSE(resp.body)
        const htmlContent = this.parseIntentResult(finalData)
        if (!htmlContent) throw new Error('AI 返回结果为空或格式不正确')

        this.downloadXlsxFromHtml(htmlContent)
        this.$Message.success('意图文档生成并下载成功')
        this.visible = false
      } catch (err) {
        console.error('执行AI意图生成失败:', err)
        this.$Message.error('AI 意图生成失败，请检查文件与网络')
      } finally {
        this.running = false
      }
    },

    async readSSE(readableStream) {
      const reader = readableStream.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''
      let lastData = null

      let isDone = false
      while (!isDone) {
        const chunk = await reader.read()
        isDone = chunk.done === true
        if (isDone) break
        buffer += decoder.decode(chunk.value, { stream: true })

        const events = buffer.split('\n\n')
        buffer = events.pop() || ''
        events.forEach(evt => {
          const lines = evt.split('\n')
          lines.forEach(line => {
            const s = line.trim()
            if (s.startsWith('data:')) {
              const jsonStr = s.replace(/^data:\s*/, '')
              if (jsonStr === '[DONE]') return
              try {
                const obj = JSON.parse(jsonStr)
                lastData = obj
              } catch (e) {
                // ignore partial frames
              }
            }
          })
        })
      }

      return lastData || {}
    },

    parseIntentResult(data) {
      try {
        if (!data) return null
        if (data.text_1) return data.text_1
        const outputs = data.outputs || (data.data && data.data.outputs)
        if (outputs) {
          if (outputs.text_1) return outputs.text_1
          for (const key in outputs) {
            if (key.includes('text') && typeof outputs[key] === 'string') {
              return outputs[key]
            }
          }
        }
        if (typeof data === 'string') {
          const parsed = JSON.parse(data)
          return parsed.text_1 || null
        }
        if (data.data && typeof data.data === 'string') {
          const parsed = JSON.parse(data.data)
          return parsed.text_1 || null
        }
      } catch (e) {
        console.warn('解析AI意图结果异常', e)
      }
      return null
    },

    downloadXlsxFromHtml(htmlContent) {
      const container = document.createElement('div')
      container.innerHTML = htmlContent
      const table = container.querySelector('table')
      if (!table) throw new Error('未找到表格内容')

      const sheet = XLSX.utils.table_to_sheet(table)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, sheet, '意图')

      // 列宽优化（按表头估计）
      const header = Array.from(table.querySelectorAll('thead th')).map(th => (th.textContent || '').trim())
      const widths = header.map(h => ({ wch: Math.max(12, h.length + 2) }))
      sheet['!cols'] = widths

      const today = new Date().toISOString().slice(0, 10)
      let fileName = this.pendingFile.name.split('.').slice(0, -1).join('.')
      fileName = `AI意图_${fileName}_${today}.xlsx`

      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-intent-generate-container {
  .section {
    margin-bottom: 16px;
    padding: 16px;
    border: 1px solid #e8eaec;
    border-radius: 6px;
    background-color: #fafafa;

    h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
      color: #17233d;
    }

    .desc {
      margin: 0 0 12px 0;
      color: #808695;
      font-size: 14px;
      line-height: 1.5;
    }

    .upload-area {
      .upload-tip {
        margin: 8px 0 0 0;
        color: #c5c8ce;
        font-size: 12px;
      }
    }
  }
}
</style>



